/**
 * CountdownStrip — reads dates and label from guest_site_config.
 * Falls back to hardcoded values if config is unavailable.
 */

import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const glassCard = {
  background: 'rgba(255,252,240,0.55)',
  backdropFilter: 'blur(16px) saturate(1.30) brightness(1.04)',
  WebkitBackdropFilter: 'blur(16px) saturate(1.30) brightness(1.04)',
  border: '1px solid rgba(200,170,60,0.45)',
  borderRadius: 20,
  boxShadow: [
    'inset 0 1.5px 0 rgba(255,255,255,0.85)',
    'inset 0 -1px 0 rgba(200,170,60,0.22)',
    '0 8px 32px rgba(140,100,20,0.12)',
    '0 0 0 0.5px rgba(200,170,60,0.20)',
  ].join(', '),
};

const CountdownStrip = () => {
  const [now, setNow] = useState(new Date());
  const [cfg, setCfg] = useState({
    deadline_label: 'Kindly RSVP by',
    rsvp_deadline:  '2026-06-15',
    event_date:     '2026-07-26',
  });

  // Read dates and label from guest_site_config
  useEffect(() => {
    fetch(`${API}/api/public/guest-site-config`)
      .then(r => r.json())
      .then(d => {
        const rd = d?.rsvp_display || {};
        setCfg(prev => ({
          deadline_label: rd.deadline_label || prev.deadline_label,
          rsvp_deadline:  rd.rsvp_deadline  || prev.rsvp_deadline,
          event_date:     rd.event_date      || prev.event_date,
        }));
      })
      .catch(() => {}); // fallback stays in state if fetch fails
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // Parse dates safely — append T12:00:00 to avoid timezone off-by-one
  const parseDate = (s) => {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T12:00:00`);
    return new Date(s);
  };

  const rsvpDate  = parseDate(cfg.rsvp_deadline);
  const eventDate = parseDate(cfg.event_date);

  const rsvpDiff   = rsvpDate  ? Math.max(0, rsvpDate  - now) : 0;
  const eventDiff  = eventDate ? Math.max(0, eventDate - now) : 0;
  const rsvpDays   = Math.floor(rsvpDiff  / 86400000);
  const eventDays  = Math.floor(eventDiff / 86400000);
  const rsvpPassed = rsvpDate ? rsvpDate < now : false;

  // Format display date
  const fmtDate = (d) => {
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="card-h-pad" style={{ width: '100%', paddingTop: 16, paddingBottom: 0, boxSizing: 'border-box' }}>
      <style>{`
        @keyframes countGlow {
          0%, 100% { text-shadow: 0 1px 0 rgba(255,255,255,0.80); }
          50%       { text-shadow: 0 0 20px rgba(140,100,20,0.35), 0 1px 0 rgba(255,255,255,0.80); }
        }
      `}</style>
      <div style={{ ...glassCard, padding: '20px 0', overflow: 'hidden', position: 'relative' }}>

        {/* ── Shared label row — both titles on the exact same line ── */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize: 13, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '2px',
              color: 'rgba(140,100,20,0.80)', margin: 0 }}>
              {rsvpPassed ? 'RSVP Closed' : cfg.deadline_label}
            </p>
          </div>
          {/* Vertical divider */}
          <div style={{ width: 1, height: 18, background: 'rgba(180,140,30,0.50)', flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize: 13, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '2px',
              color: 'rgba(140,100,20,0.80)', margin: 0 }}>
              Celebration in
            </p>
          </div>
        </div>

        {/* ── Data row ── */}
        <div style={{ display: 'flex', alignItems: 'stretch' }}>

          {/* LEFT — RSVP deadline */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: '0 20px', gap: 5 }}>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: 26, fontWeight: 600,
              color: '#1E1A14', margin: 0, letterSpacing: '0.5px', textAlign: 'center' }}>
              {fmtDate(rsvpDate)}
            </p>
            {!rsvpPassed && rsvpDays >= 0 && (
              <p style={{ fontSize: 18, color: 'rgba(80,55,10,0.92)', margin: 0,
                fontStyle: 'italic', fontFamily: "'Cormorant Garamond', serif", textAlign: 'center' }}>
                {rsvpDays > 0 ? `${rsvpDays} day${rsvpDays !== 1 ? 's' : ''} remaining` : 'Today'}
              </p>
            )}
          </div>

          {/* Vertical gold hairline */}
          <div style={{ width: 1, alignSelf: 'stretch', margin: '0',
            background: 'linear-gradient(180deg, transparent 0%, rgba(180,140,30,0.65) 20%,' +
              'rgba(180,140,30,0.65) 80%, transparent 100%)' }} />

          {/* RIGHT — Days to event */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: '0 20px', gap: 4 }}>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize: 56, fontWeight: 700,
              color: '#1E1A14', margin: 0, lineHeight: 1,
              animation: 'countGlow 3s ease-in-out infinite',
              letterSpacing: '-1px' }}>
              {eventDays}
            </p>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize: 14, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '3px',
              color: 'rgba(80,55,10,0.92)', margin: 0 }}>
              Days
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CountdownStrip;
